import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

const DEBOUNCE_MS = 120;

const AutoResizeTextFieldBase = styled.textarea<{
  $showVerticalScroll: boolean;
}>`
  width: 100%;
  max-width: 100%;
  margin-top: 5px;
  margin-bottom: 5px;
  padding: 10px 12px;
  border: 1px solid #fecc9f;
  outline-color: #fecc9f;
  border-radius: 6px;
  font-size: 18px;
  line-height: 1.35;
  caret-color: #fd954f;
  resize: none;
  overflow-x: hidden;
  overflow-y: ${(props) => (props.$showVerticalScroll ? "auto" : "hidden")};
  box-sizing: border-box;
`;

type AutoResizeTextFieldProps =
  React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export const AutoResizeTextField = React.forwardRef<
  HTMLTextAreaElement,
  AutoResizeTextFieldProps
>((props, ref) => {
  const innerRef = React.useRef<HTMLTextAreaElement | null>(null);
  const timeoutRef = React.useRef<number | null>(null);
  const [rows, setRows] = React.useState(1);
  const [showVerticalScroll, setShowVerticalScroll] = React.useState(false);

  const setCombinedRef = React.useCallback(
    (node: HTMLTextAreaElement | null) => {
      innerRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  const recalculateRows = React.useCallback((element: HTMLTextAreaElement) => {
    const previousRows = element.rows;
    element.rows = 1;

    const computedStyles = window.getComputedStyle(element);
    const computedLineHeight = parseFloat(computedStyles.lineHeight);
    const paddingTop = parseFloat(computedStyles.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyles.paddingBottom) || 0;
    const fallbackLineHeight = 24;
    const lineHeight = Number.isFinite(computedLineHeight)
      ? computedLineHeight
      : fallbackLineHeight;
    const textContentHeight = Math.max(
      0,
      element.scrollHeight - paddingTop - paddingBottom,
    );
    const requiredRows = Math.max(1, Math.ceil(textContentHeight / lineHeight));
    const nextRows = Math.min(2, requiredRows);

    element.rows = previousRows;

    setRows(nextRows);
    setShowVerticalScroll(requiredRows > 2);
  }, []);

  const scheduleRecalculate = React.useCallback(
    (element: HTMLTextAreaElement) => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = window.setTimeout(() => {
        recalculateRows(element);
        timeoutRef.current = null;
      }, DEBOUNCE_MS);
    },
    [recalculateRows],
  );

  React.useLayoutEffect(() => {
    if (innerRef.current) {
      recalculateRows(innerRef.current);
    }
  }, [props.value, recalculateRows]);

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      scheduleRecalculate(event.currentTarget);
      props.onChange?.(event);
    },
    [props, scheduleRecalculate],
  );

  return (
    <AutoResizeTextFieldBase
      {...props}
      ref={setCombinedRef}
      rows={rows}
      $showVerticalScroll={showVerticalScroll}
      spellCheck={props.spellCheck ?? false}
      onChange={handleChange}
    />
  );
});

AutoResizeTextField.displayName = "AutoResizeTextField";

AutoResizeTextField.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  spellCheck: PropTypes.bool,
};
